import { useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { Pause, Play } from "lucide-react"
import { formatFuelFraction } from "./verdictDisplay"
import { parseUtcIso } from "./parseUtcIso"
import type { TelemetryPoint } from "./fuelReportTypes"

const PLAY_TICK_MS = 150

export function interpolateAtIndex(
  points: TelemetryPoint[],
  index: number,
): { lat: number; lng: number; fuelLevel: number | null } | null {
  if (points.length === 0) return null
  const i = Math.max(0, Math.min(index, points.length - 1))
  const p = points[i]
  return { lat: p.lat, lng: p.lng, fuelLevel: p.fuelLevel }
}

function hhmmss(iso: string): string {
  const t = parseUtcIso(iso)
  return Number.isNaN(t.getTime())
    ? "--:--"
    : format(t, "HH:mm:ss", { locale: getDateFnsLocale() })
}

export function PlaybackSlider({
  points,
  index,
  onIndexChange,
}: {
  points: TelemetryPoint[]
  index: number
  onIndexChange: (i: number) => void
}) {
  const { t } = useTranslation()
  const [playing, setPlaying] = useState(false)
  // Latest index without retriggering the interval on every tick.
  const indexRef = useRef(index)
  indexRef.current = index

  useEffect(() => {
    if (!playing || points.length < 2) return
    const id = setInterval(() => {
      const next = indexRef.current + 1
      if (next >= points.length) {
        setPlaying(false)
      } else {
        onIndexChange(next)
      }
    }, PLAY_TICK_MS)
    return () => clearInterval(id)
  }, [playing, points.length, onIndexChange])

  // A new telemetry window means a new track — stop the old playback.
  useEffect(() => {
    setPlaying(false)
  }, [points])

  if (points.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        {t("reports.playback.noTelemetry")}
      </div>
    )
  }
  const current = points[Math.max(0, Math.min(index, points.length - 1))]
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            // Replay from the start when the track already finished.
            if (!playing && index >= points.length - 1) onIndexChange(0)
            setPlaying((p) => !p)
          }}
          title={
            playing ? t("reports.playback.pause") : t("reports.playback.play")
          }
          aria-label={
            playing
              ? t("reports.playback.pauseLabel")
              : t("reports.playback.playLabel")
          }
          className="text-muted-foreground hover:text-foreground"
        >
          {playing ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>
        <input
          type="range"
          min={0}
          max={points.length - 1}
          value={index}
          onChange={(e) => onIndexChange(Number(e.target.value))}
          className="w-full"
          aria-label={t("reports.playback.sliderLabel")}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{hhmmss(points[0].timestamp)}</span>
        <span className="font-medium text-foreground">
          {t("reports.playback.current", {
            time: hhmmss(current.timestamp),
            level: formatFuelFraction(current.fuelLevel),
          })}
        </span>
        <span>{hhmmss(points[points.length - 1].timestamp)}</span>
      </div>
    </div>
  )
}
